// Scheduled-poller health, derived from systemHealth/scheduledPoller.
//
// The poller (a GitHub Actions cron) writes that document at the end of EVERY
// run, including failed ones, from a step that depends on nothing pip installs.
// This module turns the document into something a person notices without
// opening GitHub: DOWN after consecutive failures, STALE when no run has
// succeeded for too long (a cron that stopped firing looks exactly like one
// that keeps failing, from the outside), and OK otherwise.

export const CONSECUTIVE_FAILURE_THRESHOLD = 2;
export const DEFAULT_INTERVAL_MINUTES = 30;
export const STALE_INTERVALS = 3; // 3 x 30 min = 90 min without a good run
export const MAX_SKEW_MS = 5 * 60000;

function toMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatAge(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return 'unknown';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'moments ago';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest ? `${hours} h ${rest} min ago` : `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function evaluatePollerHealth(data, now = Date.now()) {
  if (!data) {
    return {
      tone: 'unknown',
      alarm: true,
      headline: 'Scheduled poller: no heartbeat recorded',
      detail: 'The poller has never written a heartbeat. Until it does, nothing here can say whether it is running.',
      consecutiveFailures: 0,
      lastSuccessAgeMs: null,
      lastRunUrl: null,
    };
  }

  const consecutiveFailures = Number(data.consecutiveFailures) || 0;
  const intervalMinutes = Number(data.expectedIntervalMinutes) || DEFAULT_INTERVAL_MINUTES;
  const staleAfterMs = Math.max(STALE_INTERVALS * intervalMinutes, 90) * 60000;
  const lastSuccessMs = toMillis(data.lastSuccessAt);
  const lastRunMs = toMillis(data.lastRunAt);
  const countUnavailable = data.consecutiveFailuresCountUnavailable === true;
  // Clock skew of a few minutes between the runner and this browser is normal
  // and reads as "just now"; anything further in the future is a lie we will
  // not treat as health.
  const rawSuccessAge = lastSuccessMs === null ? null : now - lastSuccessMs;
  if (rawSuccessAge !== null && rawSuccessAge < -MAX_SKEW_MS) {
    return {
      tone: 'unknown',
      alarm: true,
      headline: 'Scheduled poller: heartbeat timestamp is in the future',
      detail: 'The last recorded success is ahead of this clock by more than five minutes; the record cannot be trusted.',
      consecutiveFailures, lastSuccessAgeMs: rawSuccessAge, lastRunAgeMs: null,
      lastRunUrl: data.lastRunUrl || null, lastFailureUrl: data.lastFailureRunUrl || data.lastRunUrl || null,
      intervalMinutes,
    };
  }
  const lastSuccessAgeMs = rawSuccessAge === null ? null : Math.max(0, rawSuccessAge);
  const lastRunAgeMs = lastRunMs === null ? null : Math.max(0, now - lastRunMs);
  const lastRunUrl = data.lastRunUrl || null;
  const lastFailureUrl = data.lastFailureRunUrl || lastRunUrl;
  const lastSuccessText = lastSuccessAgeMs === null ? 'never' : formatAge(lastSuccessAgeMs);
  const base = { consecutiveFailures, lastSuccessAgeMs, lastRunAgeMs, lastRunUrl, lastFailureUrl, intervalMinutes };

  if (consecutiveFailures >= 1 && countUnavailable) {
    // The poller failed AND could not ask GitHub how many times in a row.
    // Unknown depth of failure is treated as the alarm case, never the mild one.
    return {
      ...base,
      tone: 'down',
      alarm: true,
      headline: 'Scheduled poller DOWN: last run failed and the failure count is unavailable',
      detail: `The heartbeat could not read the run history, so this may be the first failure or the fortieth. Last successful run ${lastSuccessText}` +
        (data.lastFailureStep ? `; failing at "${data.lastFailureStep}".` : '.'),
    };
  }

  if (consecutiveFailures >= CONSECUTIVE_FAILURE_THRESHOLD) {
    return {
      ...base,
      tone: 'down',
      alarm: true,
      headline: `Scheduled poller DOWN: ${consecutiveFailures} consecutive failed runs`,
      detail: `Nothing is being sent or read from mailboxes. Last successful run ${lastSuccessText}` +
        (data.lastFailureStep ? `; failing at "${data.lastFailureStep}".` : '.'),
    };
  }

  const stale = lastSuccessAgeMs === null || lastSuccessAgeMs > staleAfterMs;
  if (stale) {
    return {
      ...base,
      tone: 'stale',
      alarm: true,
      headline: `Scheduled poller STALE: no successful run for ${lastSuccessText === 'never' ? 'as long as records go' : lastSuccessText.replace(' ago', '')}`,
      detail: `Expected every ${intervalMinutes} min. ` +
        (lastRunAgeMs === null ? 'No run has been recorded.' : `Last run of any kind ${formatAge(lastRunAgeMs)}.`),
    };
  }

  if (consecutiveFailures === 1) {
    return {
      ...base,
      tone: 'degraded',
      alarm: false,
      headline: 'Scheduled poller: last run failed',
      detail: `One failure so far; a second in a row raises the alarm. Last successful run ${lastSuccessText}` +
        (data.lastFailureStep ? `; failed at "${data.lastFailureStep}".` : '.'),
    };
  }

  return {
    ...base,
    tone: 'ok',
    alarm: false,
    headline: `Scheduled poller running: last successful run ${lastSuccessText}`,
    detail: `Runs every ${intervalMinutes} min` + (data.lastRunRevision ? ` on revision ${data.lastRunRevision}.` : '.'),
  };
}
